// src/store/slices/leavePolicySlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import leavePolicyApi from 'api/leavePolicyApi';

export const fetchLeavePolicies = createAsyncThunk(
  'leavePolicy/fetchLeavePolicies',
  async ({ page = 1, pageSize = 10, search = '' }, { rejectWithValue }) => {
    try {
      const response = await leavePolicyApi.getAll(page, pageSize, search);
      return response;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load leave policies';
      return rejectWithValue(msg);
    }
  }
);

export const createLeavePolicy = createAsyncThunk(
  'leavePolicy/createLeavePolicy',
  async (data, { rejectWithValue }) => {
    try {
      const response = await leavePolicyApi.create(data);
      return response;
    } catch (err) {
      console.log("err", err);
      const msg = err.response?.data?.error[0] || 'Failed to create leave policy';
      return rejectWithValue(msg);
    }
  }
);

export const updateLeavePolicy = createAsyncThunk(
  'leavePolicy/updateLeavePolicy',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const response = await leavePolicyApi.update(id, data);
      return response;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update leave policy';
      return rejectWithValue(msg);
    }
  }
);

export const deleteLeavePolicy = createAsyncThunk(
  'leavePolicy/deleteLeavePolicy',
  async (id, { rejectWithValue }) => {
    try {
      await leavePolicyApi.delete(id);
      return id;
    } catch (err) {
      const msg =
        err.response?.data?.error || err.response?.data?.detail || 'Failed to delete leave policy';
      return rejectWithValue(msg);
    }
  }
);

export const exportLeavePolicies = createAsyncThunk(
  'leavePolicy/exportLeavePolicies',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await leavePolicyApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leave-policies_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

export const importPreview = createAsyncThunk(
  'leavePolicy/importPreview',
  async (file, { rejectWithValue }) => {
    try {
      const data = await leavePolicyApi.importPreview(file);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Preview failed');
    }
  }
);

export const importConfirm = createAsyncThunk(
  'leavePolicy/importConfirm',
  async (preview, { rejectWithValue, dispatch }) => {
    try {
      await leavePolicyApi.importConfirm(preview);
      dispatch(fetchLeavePolicies({ page: 1, pageSize: 10 }));
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Import failed');
    }
  }
);

const leavePolicySlice = createSlice({
  name: 'leavePolicy',
  initialState: {
    leavePolicies: [],
    pagination: { next: null, previous: null, count: 0, page_size: 10 },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeavePolicies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeavePolicies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leavePolicies = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchLeavePolicies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createLeavePolicy.fulfilled, (state, action) => {
        state.leavePolicies.unshift(action.payload);
        state.pagination.count += 1;
      })
      .addCase(updateLeavePolicy.fulfilled, (state, action) => {
        const idx = state.leavePolicies.findIndex(p => p.id === action.payload.id);
        if (idx !== -1) state.leavePolicies[idx] = action.payload;
      })
      .addCase(deleteLeavePolicy.fulfilled, (state, action) => {
        state.leavePolicies = state.leavePolicies.filter(p => p.id !== action.payload);
        state.pagination.count -= 1;
      })
      .addCase(exportLeavePolicies.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(importPreview.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(importPreview.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(importPreview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(importConfirm.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(importConfirm.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(importConfirm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = leavePolicySlice.actions;
export default leavePolicySlice.reducer;